import { asTyped } from "./as-typed";

/**
 * Pasted HTML, kept for what it says and stripped of how it looked.
 *
 * The reply box used to paste plain text and nothing else. That was a reaction
 * to Word and web pages, which put their fonts, sizes, colours and nested
 * layout on the clipboard along with the words, and made a reply look nothing
 * like the rest of the desk's mail. It worked, and it threw out the baby with
 * the bathwater: a list of steps pasted from a supplier's email arrived as a
 * run of lines with the bullets gone, the one word somebody had bolded was no
 * longer bold, and a link was its text. The desk asked for those back -- what
 * they did not want was somebody else's typeface.
 *
 * So the line is drawn between structure and appearance. Kept: bold, italic,
 * underline, bulleted and numbered lists, links and the line breaks between
 * paragraphs -- exactly what the toolbar can make, so a pasted reply is one the
 * agent could have typed. Dropped: every attribute but a link's address, and
 * every element outside that list, whose text is kept and whose wrapping is
 * not. Fonts, sizes, colours, backgrounds, classes and Word's conditional
 * markup all go that way, because none of them survives being unwrapped.
 *
 * Bold is read from a `font-weight` as well as from `<b>`, because Google Docs
 * marks it no other way -- and wraps a whole copy in `<b style="font-weight:
 * normal">`, which is why the weight decides over the tag. A heading becomes a
 * bold line: its size is appearance, but that it stood out is not.
 *
 * Paragraphs become the reply box's own lines, through `asTyped` -- the same
 * translation canned snippets get, so a pasted paragraph sits a blank line from
 * the next just as one typed with Enter twice does. Word, Outlook and Google
 * Docs are the exception: each line there is already a paragraph, and blank
 * lines are empty paragraphs of their own, so those are taken line for line
 * rather than given a gap each and coming out double-spaced.
 *
 * Word's lists are not lists in its HTML. Each item is a paragraph with an
 * `mso-list` style, its bullet a literal "·" in a span the browser is meant to
 * ignore. Unwrapped naively that leaves a dot and some spaces in front of each
 * item; so the marker is dropped, and consecutive items are rebuilt into a real
 * list, nested by the level Word records and numbered when the marker was a
 * number.
 *
 * Tables are not this module's: `paste-table.ts` sees the clipboard first and
 * rebuilds a real one. One that reaches here was too small to be a grid -- a
 * layout cell round some text -- and is unwrapped like anything else.
 *
 * Nothing here is the security boundary -- the server sanitises on save. But
 * it is near enough: no attribute but `href` leaves, and that only for http,
 * https and mailto.
 */
export function cleanPastedHtml(html: string, text = ""): string | null {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const flow = new Flow(LINE_SOURCES.test(html));
  flow.walk(doc.body, PLAIN);
  const blocks = flow.finish();
  if (blocks.length === 0) return null;

  /*
   * A single line goes in as inline content. Wrapped in a line of its own, a
   * word pasted into the middle of a sentence would break the sentence in two.
   * Its edges keep a space where the plain text has one: a word copied with
   * the space after it is meant to go in with that space, and the markup's
   * own whitespace, which the plain text does not have, is not.
   */
  const [only] = blocks;
  if (blocks.length === 1 && only?.kind === "line") {
    const inner = renderRuns(only.runs);
    if (!inner) return null;
    return `${/^ /.test(text) ? " " : ""}${inner}${/ $/.test(text) ? " " : ""}`;
  }

  return asTyped(blocks.map(renderBlock).join(""));
}

/* Where every line is its own paragraph: Word and Outlook desktop, by their
   Office namespace or their Mso classes, and Google Docs, by its wrapper. */
const LINE_SOURCES = /urn:schemas-microsoft-com:office|class="?Mso|docs-internal-guid/i;

interface Marks {
  b: boolean;
  i: boolean;
  u: boolean;
  href: string | null;
}

type Run = { text: string; marks: Marks } | { br: true };

interface Item {
  runs: Run[];
  lists: ListBlock[];
}

interface ListBlock {
  kind: "list";
  ordered: boolean;
  items: Item[];
}

interface LineBlock {
  kind: "line";
  paragraph: boolean;
  /* An empty line on purpose: a blank line in a line-for-line source. */
  blank?: boolean;
  runs: Run[];
}

type Block = LineBlock | ListBlock;

const PLAIN: Marks = { b: false, i: false, u: false, href: null };

/* Never content: dropped with everything inside them. */
const SKIP = new Set([
  "STYLE", "SCRIPT", "HEAD", "TITLE", "META", "LINK", "TEMPLATE", "NOSCRIPT",
  "IMG", "PICTURE", "VIDEO", "AUDIO", "SVG", "CANVAS", "IFRAME", "OBJECT",
  "EMBED", "INPUT", "BUTTON", "SELECT", "TEXTAREA",
]);

/* Elements whose edges are line breaks. */
const BLOCK = new Set([
  "P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE", "LI",
  "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TD", "TH", "CAPTION", "SECTION",
  "ARTICLE", "HEADER", "FOOTER", "NAV", "ASIDE", "MAIN", "FIGURE",
  "FIGCAPTION", "ADDRESS", "DL", "DT", "DD", "HR", "CENTER", "FORM",
  "FIELDSET", "LEGEND", "DETAILS", "SUMMARY",
]);

/* The blocks that are paragraphs, set a blank line apart, where the source
   uses paragraphs that way. The rest are lines. */
const PARAGRAPH = new Set([
  "P", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE", "ADDRESS",
  "FIGCAPTION",
]);

const BLOCK_SELECTOR = [...BLOCK].join(",");

/**
 * The document, read in order into lines and lists.
 *
 * Inline content collects as runs -- text with the marks in force over it --
 * and every block edge ends the line in progress. Runs rather than a cloned
 * tree because the formatting and the line structure cross: Google Docs puts
 * its paragraphs inside a `<b>`, and a tree copied as it came would put the
 * reply box's lines inside it too.
 */
class Flow {
  private readonly blocks: Block[] = [];
  private runs: Run[] = [];
  /* The open blocks, innermost last: what kind of line a flush makes. */
  private readonly stack: string[] = [];
  /* A Word list in progress, by level; null when the last block was not one. */
  private word: ListBlock[] | null = null;

  constructor(private readonly lineForLine: boolean) {}

  walk(node: Node, marks: Marks): void {
    if (node.nodeType === Node.TEXT_NODE) {
      addText(this.runs, node.textContent ?? "", marks);
      return;
    }
    if (!(node instanceof HTMLElement)) return;

    const tag = node.tagName;
    if (SKIP.has(tag) || isWordMarker(node)) return;

    if (tag === "BR") {
      this.runs.push({ br: true });
      return;
    }

    const next = marksOf(node, marks);

    if (tag === "UL" || tag === "OL") {
      this.endLine();
      this.push(listOf(node, next));
      return;
    }

    if (isWordListItem(node)) {
      this.endLine();
      this.wordItem(node, next);
      return;
    }

    if (!BLOCK.has(tag)) {
      node.childNodes.forEach((child) => this.walk(child, next));
      return;
    }

    this.endLine();
    this.stack.push(tag);
    node.childNodes.forEach((child) => this.walk(child, next));

    /*
     * An empty block is a blank line where the source writes them that way --
     * Outlook's `<p class=MsoNormal>&nbsp;</p>` -- and nothing anywhere else,
     * where it is a spacer somebody styled into a gap. Only a block with no
     * blocks inside it, or the wrappers round a paragraph would count as well.
     */
    if (!meaningful(this.runs)) {
      this.runs = [];
      if (
        this.lineForLine &&
        (tag === "P" || tag === "DIV") &&
        !node.querySelector(BLOCK_SELECTOR)
      ) {
        this.push({ kind: "line", paragraph: false, blank: true, runs: [] });
      }
    } else {
      this.endLine();
    }
    this.stack.pop();
  }

  finish(): Block[] {
    this.endLine();
    /* Trailing blank lines are the source's bottom margin, not the agent's. */
    while (this.blocks.at(-1)?.kind === "line" && (this.blocks.at(-1) as LineBlock).blank) {
      this.blocks.pop();
    }
    return this.blocks;
  }

  private endLine(): void {
    const runs = this.runs;
    this.runs = [];
    if (!meaningful(runs)) return;

    /* A line of nothing but breaks is a blank line, in either kind of source:
       it is how Gmail and Google Docs write one. In a source of paragraphs the
       gaps come from the paragraphs, and one more would double them. */
    if (runs.every((run) => "br" in run)) {
      if (this.lineForLine) {
        this.push({ kind: "line", paragraph: false, blank: true, runs: [] });
      }
      return;
    }

    const tag = this.stack.at(-1);
    this.push({
      kind: "line",
      paragraph: !this.lineForLine && tag !== undefined && PARAGRAPH.has(tag),
      runs,
    });
  }

  private push(block: Block): void {
    this.word = null;
    this.blocks.push(block);
  }

  /**
   * One Word list paragraph, added to the list it continues.
   *
   * Word records the level as `level2` in the item's `mso-list` style; the
   * open lists are kept by level so a deeper item nests in the item above it
   * and a shallower one returns to its own list. A change from bullets to
   * numbers at the same level starts a new list there, as Word drew it.
   */
  private wordItem(node: HTMLElement, marks: Marks): void {
    const style = node.getAttribute("style") ?? "";
    const level = Math.max(1, Number(/level(\d+)/i.exec(style)?.[1] ?? 1));
    const marker =
      [...node.querySelectorAll<HTMLElement>("[style]")]
        .find(isWordMarker)
        ?.textContent?.replace(/\s+/g, "") ?? "";
    const ordered = /^\(?([0-9]{1,3}|[a-zA-Z]|[ivxIVX]{1,4})[.)]$/.test(marker);
    const item = itemOf(node, marks);

    const stack = this.word ?? [];
    while (stack.length > level) stack.pop();
    if (stack.length === level && stack[level - 1]!.ordered !== ordered) stack.pop();
    while (stack.length < level) {
      const list: ListBlock = { kind: "list", ordered, items: [] };
      const parent = stack.at(-1);
      if (parent) {
        let last = parent.items.at(-1);
        if (!last) {
          last = { runs: [], lists: [] };
          parent.items.push(last);
        }
        last.lists.push(list);
      } else {
        this.blocks.push(list);
      }
      stack.push(list);
    }
    stack.at(-1)!.items.push(item);
    this.word = stack;
  }
}

/** A real list, its items cleaned and its nesting kept. */
function listOf(node: HTMLElement, marks: Marks): ListBlock {
  const list: ListBlock = {
    kind: "list",
    ordered: node.tagName === "OL",
    items: [],
  };
  for (const child of node.childNodes) {
    if (child instanceof HTMLElement && (child.tagName === "UL" || child.tagName === "OL")) {
      /* A list straight inside a list, which some editors write for an
         indent: it belongs to the item above it. */
      let last = list.items.at(-1);
      if (!last) {
        last = { runs: [], lists: [] };
        list.items.push(last);
      }
      last.lists.push(listOf(child, marks));
    } else if (child instanceof HTMLElement && child.tagName === "LI") {
      list.items.push(itemOf(child, marksOf(child, marks)));
    } else if (child.textContent?.trim()) {
      list.items.push(itemOf(child, marks));
    }
  }
  return list;
}

/**
 * One list item's content: its inline text, with any blocks inside it run
 * together on breaks, and its own sub-lists. Used for a Word list paragraph
 * as well, whose marker span `walk` would otherwise have to skip by hand.
 */
function itemOf(node: Node, marks: Marks): Item {
  const item: Item = { runs: [], lists: [] };
  const visit = (current: Node, inherited: Marks): void => {
    if (current.nodeType === Node.TEXT_NODE) {
      addText(item.runs, current.textContent ?? "", inherited);
      return;
    }
    if (!(current instanceof HTMLElement)) return;
    const tag = current.tagName;
    if (SKIP.has(tag) || isWordMarker(current)) return;
    if (tag === "BR") {
      item.runs.push({ br: true });
      return;
    }
    const next = marksOf(current, inherited);
    if (tag === "UL" || tag === "OL") {
      item.lists.push(listOf(current, next));
      return;
    }
    const block = BLOCK.has(tag);
    if (block && meaningful(item.runs) && !("br" in item.runs.at(-1)!)) {
      item.runs.push({ br: true });
    }
    current.childNodes.forEach((child) => visit(child, next));
  };

  if (node instanceof HTMLElement && node.tagName !== "LI" && !isWordListItem(node)) {
    visit(node, marks);
  } else {
    node.childNodes.forEach((child) => visit(child, marks));
  }
  return item;
}

/** The marks in force inside an element, from its tag and its style. */
function marksOf(node: HTMLElement, marks: Marks): Marks {
  const tag = node.tagName;
  const style = node.getAttribute("style") ?? "";
  let { b, i, u, href } = marks;

  if (tag === "B" || tag === "STRONG" || /^H[1-6]$/.test(tag)) b = true;
  if (tag === "I" || tag === "EM") i = true;
  if (tag === "U" || tag === "INS") u = true;

  /* The style decides over the tag: see the header on Google Docs. */
  const weight = /font-weight\s*:\s*([\w-]+)/i.exec(style)?.[1]?.toLowerCase();
  if (weight) b = weight === "bold" || weight === "bolder" || Number(weight) >= 600;
  const fontStyle = /font-style\s*:\s*(\w+)/i.exec(style)?.[1]?.toLowerCase();
  if (fontStyle) i = fontStyle === "italic" || fontStyle === "oblique";
  if (/text-decoration(?:-line)?\s*:[^;]*underline/i.test(style)) u = true;

  if (tag === "A") href = safeHref(node.getAttribute("href")) ?? href;

  return { b, i, u, href };
}

/** A link worth keeping: absolute, and to somewhere a reader can go. */
function safeHref(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value.trim());
    return ["http:", "https:", "mailto:"].includes(url.protocol) ? url.href : null;
  } catch {
    // Relative, or not a URL at all: there is no page for it to be relative to.
    return null;
  }
}

/* Word's list marker: the "·" or "1." it draws, in a span marked to ignore. */
function isWordMarker(node: HTMLElement): boolean {
  return /mso-list\s*:\s*ignore/i.test(node.getAttribute("style") ?? "");
}

/* A Word list paragraph: `mso-list: l0 level1 lfo1`. */
function isWordListItem(node: HTMLElement): boolean {
  return /mso-list\s*:\s*l\d+/i.test(node.getAttribute("style") ?? "");
}

/**
 * Text, with HTML's whitespace rule applied, joined to the runs so far.
 *
 * Non-breaking spaces count as spaces. Chrome writes one at every inline edge
 * of a copied selection and Word pads with runs of them; kept, a pasted
 * paragraph could not wrap there, in the box or in the customer's mail.
 */
function addText(runs: Run[], raw: string, marks: Marks): void {
  const text = raw.replace(/[ \t\r\n\f\u00a0]+/g, " ");
  if (text === "") return;
  /* Whitespace at the start of a line is the indentation of the source's
     markup, not anything anybody typed. */
  if (text === " " && !meaningful(runs)) return;
  runs.push({ text, marks });
}

function meaningful(runs: Run[]): boolean {
  return runs.some((run) => "br" in run || run.text.trim() !== "");
}

function renderBlock(block: Block): string {
  if (block.kind === "list") return renderList(block);
  if (block.blank) return "<div><br></div>";
  const inner = renderRuns(block.runs);
  return block.paragraph ? `<p>${inner}</p>` : `<div>${inner}</div>`;
}

function renderList(list: ListBlock): string {
  const tag = list.ordered ? "ol" : "ul";
  const items = list.items
    .map((item) => `<li>${renderRuns(item.runs) || (item.lists.length ? "" : "<br>")}${item.lists.map(renderList).join("")}</li>`)
    .join("");
  return `<${tag}>${items}</${tag}>`;
}

/**
 * A line's runs as HTML: trimmed at both ends, a trailing break dropped (a
 * block ends a line already), and each run wrapped in the marks it carries.
 */
function renderRuns(input: Run[]): string {
  const runs = [...input];
  while (runs.length && "br" in runs.at(-1)!) runs.pop();
  while (runs.length && "br" in runs[0]!) runs.shift();

  const first = runs[0];
  if (first && "text" in first) runs[0] = { ...first, text: first.text.replace(/^\s+/, "") };
  const last = runs.at(-1);
  if (last && "text" in last) runs[runs.length - 1] = { ...last, text: last.text.replace(/\s+$/, "") };

  let out = "";
  let pending: { text: string; marks: Marks } | null = null;
  const flush = (): void => {
    if (pending && pending.text) out += wrap(pending.text, pending.marks);
    pending = null;
  };
  for (const run of runs) {
    if ("br" in run) {
      flush();
      out += "<br>";
    } else if (pending && sameMarks(pending.marks, run.marks)) {
      pending.text += run.text;
    } else {
      flush();
      pending = { ...run };
    }
  }
  flush();
  return out;
}

function sameMarks(a: Marks, b: Marks): boolean {
  return a.b === b.b && a.i === b.i && a.u === b.u && a.href === b.href;
}

function wrap(text: string, marks: Marks): string {
  let html = escapeHtml(text);
  if (marks.u) html = `<u>${html}</u>`;
  if (marks.i) html = `<i>${html}</i>`;
  if (marks.b) html = `<b>${html}</b>`;
  if (marks.href) html = `<a href="${escapeHtml(marks.href)}">${html}</a>`;
  return html;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
